'use client'

import {
  createContext,
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  type OfferShippingsV1,
  type OfferV11,
  type PimProductV11,
  type PimProductWithVariantsV1,
  type PrescriptionPrescriptionTypeEnum,
  type SuccessGetCarriers,
} from '@redteclab/api/clients/bully'

import { setDebugData } from '../../debug'
import { type SeoBreadcrumbListItems } from '../../seo'
import { type ProductPageConfigutation } from '../config/getPerTenantProductPageConfiguration'
import { productAccessorGetDefaultPrescriptionType } from '../model/productAccessor/productAccessorGetDefaultPrescriptionType'
import { productAccessorGetOfferByIdOrDefault } from '../model/productAccessor/productAccessorGetOfferByIdOrDefault'
import { productAccessorGetProductTemplate } from '../model/productAccessor/productAccessorGetProductTemplate'
import { productAccessorGetProductVariantForCurrentUpid } from '../model/productAccessor/productAccessorGetProductVariantForCurrentUpid'
import { productAccessorIsMasterPage } from '../model/productAccessor/productAccessorIsMasterPage'
import { productAccessorIsRxOrOtxTemplate } from '../model/productAccessor/productAccessorIsRxOrOtxTemplate'
import { type PRODUCT_TEMPLATES } from '../model/productTemplates'
import { type FaqItemWithParsedAnswer } from '../productPageDataAssembly/assembleFaqs'
import { type PromotionProduct } from '../productPageDataAssembly/assembleOfferPromotionData'
import { type ProductPageReviewData } from '../productPageDataAssembly/assembleReviews'
import {
  type RxByPrincipleControls,
  useRxByPrincipleControls,
} from '../rx-by-principle'
import { type ProductPageSocialMediaConfiguration } from '../social-media/config/ProductPageSocialMediaConfiguration'

import { useGetProductPageContextData } from './product-page-context/useGetProductPageContextData'
import {
  type ProductPageContextAttributeSelectionControls,
  useProductPageAttributeSelectionControls,
} from './product-page-context/useProductPageAttributeSelectionControls'
import {
  type ProductPageNowDetails,
  useProductPageNowDetails,
} from './product-page-context/useProductPageNowDetails'
import { useQuantitySelectionControls } from './product-page-context/useQuantitySelectionControls'
import { type ProductPageOdsConfig } from './ProductPageProps'

type ProductPageContextValue = {
  attributeSelectionControls: ProductPageContextAttributeSelectionControls
  breadcrumbs: SeoBreadcrumbListItems | null
  channel: string
  currentOffer?: OfferV11
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  currentVariant: PimProductV11
  faqs: FaqItemWithParsedAnswer[] | null
  followUpProduct: PimProductWithVariantsV1 | null
  isMasterPage: boolean
  isRxTemplate: boolean
  nowDetails: ProductPageNowDetails
  offerId: string | null
  product: PimProductWithVariantsV1
  productPageConfig: ProductPageConfigutation
  productPageOdsConfig: ProductPageOdsConfig | null
  productTemplate: PRODUCT_TEMPLATES
  promotionProduct: PromotionProduct
  quantitySelectionControls: {
    quantity: number
    setQuantity: Dispatch<SetStateAction<number>>
  }
  reviewData: ProductPageReviewData
  rxByPrincipleControls: RxByPrincipleControls
  setCurrentPrescriptionType: Dispatch<
    SetStateAction<PrescriptionPrescriptionTypeEnum>
  >
  shipping: {
    marketplaceDeliveryCosts: OfferShippingsV1[] | null
    saeShippingCosts: SuccessGetCarriers
  }
  socialMediaConfig: ProductPageSocialMediaConfiguration[]
}

const ProductPageContext = createContext<ProductPageContextValue | undefined>(
  undefined,
)
/**
 * products are changed clientside, so state of currentPrescription is kept.
 * That means if you on OTC with noPrescription and go e.g. through search to some prescription product,
 * "noPrescription" is still on state.
 * This hooks resets the prescriptionType to default whenever product changes.
 */
const useResetPrescriptionTypeOnProductFamilyChange = ({
  currentPrescriptionType,
  product,
  productTemplate,
  setCurrentPrescriptionType,
}: {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  product: PimProductWithVariantsV1
  productTemplate: PRODUCT_TEMPLATES
  setCurrentPrescriptionType: (value: PrescriptionPrescriptionTypeEnum) => void
}): void => {
  /**
   * variants of same product have same variant_code, so we need to run our logic only for case
   * we need to update only when product totally changed
   */
  const currentIdentifier = product.variant_code ?? product.upid
  const [previousIdentifier, setPreviousIdentifier] =
    useState(currentIdentifier)

  if (previousIdentifier === currentIdentifier) {
    return
  }
  setPreviousIdentifier(currentIdentifier)

  const defaultPrescriptionType = productAccessorGetDefaultPrescriptionType({
    product,
    template: productTemplate,
  })
  if (currentPrescriptionType === defaultPrescriptionType) {
    return
  }
  setCurrentPrescriptionType(defaultPrescriptionType)
}

type ProductPageContextProviderProps = {
  breadcrumbs: SeoBreadcrumbListItems | null
  channel: string
  children: ReactNode
  faqs: FaqItemWithParsedAnswer[] | null
  followUpProduct: PimProductWithVariantsV1 | null
  pageParams: {
    master: string | null
    offerId: string | null
  }
  product: PimProductWithVariantsV1
  productPageConfig: ProductPageConfigutation
  productPageOdsConfig: ProductPageOdsConfig | null
  promotionProduct: PromotionProduct
  reviewData: ProductPageReviewData
  shipping: ProductPageContextValue['shipping']
  socialMediaConfig: ProductPageSocialMediaConfiguration[]
}

export const ProductPageContextProvider: FC<
  ProductPageContextProviderProps
  // eslint-disable-next-line max-lines-per-function -- coordinates other hooks, ok
> = ({
  breadcrumbs,
  channel,
  children,
  faqs,
  followUpProduct,
  pageParams,
  product,
  productPageConfig,
  productPageOdsConfig,
  promotionProduct,
  reviewData,
  shipping,
  socialMediaConfig,
}) => {
  const { isMarketplace, tenant } = useGetProductPageContextData()

  const currentVariant = useMemo(() => {
    return productAccessorGetProductVariantForCurrentUpid(product)
  }, [product])
  const isMasterPage = productAccessorIsMasterPage(pageParams.master, product)

  const attributeSelectionControls = useProductPageAttributeSelectionControls({
    currentVariant,
    isMasterPage,
    productWithAllVariants: product,
  })
  /**
   * TODO: rename to productType later on after stable
   */
  const productTemplate = productAccessorGetProductTemplate({
    flags: currentVariant.flags,
    tenant,
  })

  /**
   * represents a default prescription type for the product.
   * User for some products is be able to select the prescription type (e.g. private insurance)
   */
  const [currentPrescriptionType, setCurrentPrescriptionType] =
    useState<PrescriptionPrescriptionTypeEnum>(() =>
      productAccessorGetDefaultPrescriptionType({
        product,
        template: productTemplate,
      }),
    )
  useResetPrescriptionTypeOnProductFamilyChange({
    currentPrescriptionType,
    product,
    productTemplate,
    setCurrentPrescriptionType,
  })
  const currentOffer = productAccessorGetOfferByIdOrDefault({
    isMarketplace,
    offerId: pageParams.offerId,
    offers: product.offers,
  })
  const quantitySelectionControls = useQuantitySelectionControls({
    currentVariant,
  })
  const nowDetails = useProductPageNowDetails({
    currentOffer,
    currentVariant,
    isMasterPage,
    tenant,
  })
  const rxByPrincipleControls = useRxByPrincipleControls({
    currentOffer,
    product,
  })
  const contextValue = useMemo(
    (): ProductPageContextValue => ({
      attributeSelectionControls,
      breadcrumbs,
      channel,
      currentOffer,
      currentPrescriptionType,
      currentVariant,
      faqs,
      followUpProduct,
      isMasterPage,
      isRxTemplate: productAccessorIsRxOrOtxTemplate(productTemplate),
      nowDetails,
      offerId: pageParams.offerId,
      product,
      productPageConfig,
      productPageOdsConfig,
      productTemplate,
      promotionProduct,
      quantitySelectionControls,
      reviewData,
      rxByPrincipleControls,
      setCurrentPrescriptionType,
      shipping,
      socialMediaConfig,
    }),
    [
      attributeSelectionControls,
      breadcrumbs,
      currentOffer,
      currentPrescriptionType,
      faqs,
      isMasterPage,
      productTemplate,
      pageParams.offerId,
      product,
      promotionProduct,
      currentVariant,
      reviewData,
      setCurrentPrescriptionType,
      shipping,
      socialMediaConfig,
      quantitySelectionControls,
      productPageOdsConfig,
      channel,
      followUpProduct,
      nowDetails,
      productPageConfig,
      rxByPrincipleControls,
    ],
  )
  useEffect(() => {
    setDebugData('product', contextValue.product)
  }, [contextValue.product])

  return (
    <ProductPageContext.Provider value={contextValue}>
      {children}
    </ProductPageContext.Provider>
  )
}
export const useProductPageContext = (): ProductPageContextValue => {
  const contextValue = useContext(ProductPageContext)
  if (contextValue === undefined) {
    throw new Error(
      'useProductPageContext must be used within a ProductPageContextProvider',
    )
  }

  return contextValue
}
